import { useState, useEffect } from "react";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { fetchDealScore } from "../api/client";
import type { DealScore, ScoreFactor } from "../api/client";

interface Props {
  dealId: string;
}

function gradeColor(grade: string): string {
  if (grade === "A") return "bg-forest-700 text-white";
  if (grade === "B+" || grade === "B") return "bg-blue-600 text-white";
  if (grade === "C+" || grade === "C") return "bg-honey-600 text-white";
  return "bg-red-700 text-white";
}

function gradeAccent(grade: string): string {
  if (grade === "A") return "#2D5016";
  if (grade === "B+" || grade === "B") return "#3B7DD8";
  if (grade === "C+" || grade === "C") return "#C8860A";
  return "#B91C1C";
}

function barColor(score: number): string {
  if (score >= 90) return "bg-forest-600";
  if (score >= 70) return "bg-blue-500";
  if (score >= 50) return "bg-honey-500";
  return "bg-red-600";
}

function shortName(name: string): string {
  const map: Record<string, string> = {
    "Occupancy Risk": "Occupancy",
    "Lease Term Risk": "Lease Term",
    "Tenant Concentration": "Concentration",
    "Rollover Exposure": "Rollover",
    "Expense Ratio": "Expense",
    "Reimbursement Coverage": "Reimburse",
    "NOI Growth": "NOI Growth",
    "Debt Coverage": "Debt",
  };
  return map[name] || name;
}

export default function DealScoreCard({ dealId }: Props) {
  const [score, setScore] = useState<DealScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchDealScore(dealId)
      .then((data) => {
        if (!cancelled && data.overall_score != null && data.factors) {
          setScore(data);
        }
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-grove-300 p-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (!score) {
    if (error) {
      return (
        <div className="bg-white rounded-xl border border-grove-300 p-4 text-sm text-bark-400">
          Deal Score unavailable — projection may be incomplete.
        </div>
      );
    }
    return null;
  }

  const radarData = score.factors.map((f) => ({
    name: shortName(f.name),
    score: f.score,
    fullMark: 100,
  }));

  const accent = gradeAccent(score.letter_grade);

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm print:shadow-none">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-grove-200">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-lg ${gradeColor(score.letter_grade)}`}
          >
            {score.letter_grade}
          </div>
          <div className="relative group">
            <div className="flex items-center gap-1.5">
              <h3 className="font-display text-base font-semibold text-bark-800">
                Deal Score
              </h3>
              <Info className="w-3.5 h-3.5 text-bark-300 group-hover:text-bark-500 transition-colors cursor-help" />
            </div>
            <p className="text-sm text-bark-400 tabular-nums whitespace-nowrap">
              {score.overall_score.toFixed(0)} / 100
            </p>
            <div className="absolute left-0 top-full mt-2 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 z-20">
              <div className="bg-bark-800 text-white text-xs rounded-lg p-3 w-72 shadow-lg leading-relaxed">
                <p className="font-semibold mb-1.5">Deal Score rates this property across 8 risk factors:</p>
                <p className="mb-1.5">Tenant Credit, Lease Term, Rollover Concentration, Occupancy, Rent-to-Market, Expense Recovery, Location Risk, and Capital Requirements.</p>
                <p className="text-bark-300">A = Low Risk (90-100) &middot; B = Moderate (75-89) &middot; C = Elevated (60-74) &middot; D = High (&lt;60)</p>
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-bark-400 hover:text-bark-600 transition-colors print:hidden"
        >
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {/* Radar Chart */}
      <div className="px-4 py-3">
        <ResponsiveContainer width="100%" height={240}>
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
            <PolarGrid stroke="#D4D0C8" />
            {/* @ts-expect-error recharts typing mismatch with React 19 */}
            <PolarAngleAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#2C2C28" }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: "#999" }}
              tickCount={5}
            />
            <Radar
              name="Score"
              dataKey="score"
              stroke={accent}
              fill={accent}
              fillOpacity={0.2}
              strokeWidth={2}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Expandable detail section */}
      {expanded && (
        <div className="border-t border-grove-200 px-6 py-4 space-y-3">
          {score.factors.map((f: ScoreFactor) => (
            <div key={f.name} className="flex items-center gap-3">
              <div className="w-36 sm:w-44 shrink-0">
                <p className="text-sm font-medium text-bark-700 truncate">
                  {f.name}
                </p>
                <p className="text-xs text-bark-400 truncate">{f.label}</p>
              </div>
              <div className="flex-1 h-3 bg-grove-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor(f.score)}`}
                  style={{ width: `${f.score}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-bark-700 w-8 text-right tabular-nums">
                {f.score}
              </span>
            </div>
          ))}
          <p className="text-xs text-bark-300 pt-2">
            Weighted across 8 dimensions. Weight shown as thickness contribution
            to overall score.
          </p>
        </div>
      )}
    </div>
  );
}
